import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import useAppUser from '../../hooks/useAppUser';
import type { StoredConsentSummary } from './legalConfig';
import { needsReacceptance } from './legalConfig';

function normalizeTimestampLike(value: unknown): string | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'object' && value !== null) {
    const toDate = (value as { toDate?: () => Date }).toDate;
    if (typeof toDate === 'function') {
      return toDate.call(value).toISOString();
    }
  }

  return null;
}

export function useLegalConsentSummary() {
  const { uid } = useAppUser();

  return useQuery<StoredConsentSummary | null>({
    queryKey: ['legal-consent-summary', uid],
    enabled: Boolean(uid),
    queryFn: async () => {
      if (!uid) {
        return null;
      }

      const snapshot = await getDoc(doc(db, 'users', uid));
      if (!snapshot.exists()) {
        return null;
      }

      const summary = (snapshot.data().legalConsents?.current ?? null) as StoredConsentSummary | null;
      if (!summary) {
        return null;
      }

      return {
        ...summary,
        marketingConsentUpdatedAt: normalizeTimestampLike(summary.marketingConsentUpdatedAt),
        analyticsConsentUpdatedAt: normalizeTimestampLike(summary.analyticsConsentUpdatedAt),
        needsReacceptance: needsReacceptance(summary),
      };
    },
    staleTime: 60 * 1000,
  });
}